'use client'

import { Box } from '@mui/joy'
import { theme as customTheme } from '@ui/style'
import { pxToRem } from '@ui/style/muiTheme'
import ContentCard from '@components/content/ContentCard'
import UIDivider from '@components/ui/UIDivider'
import color from '@ui/style/color.theme'
import SubSectionTitle from '@page/article/_components/common/SubSectionTitle'
import { ArticleListResponseVo } from '@schemas/non-auth'
import { getRelativeTime } from '@utils/date'
import getBulletColor from '@utils/getBulletColor'
import { getArticleLink } from '@utils/getLink'
import { Fragment } from 'react'
import Carousel from '@components/common/Carousel/Carousel'
import { imgDefaultH } from 'src/shared/variables'

export interface RelatedArticleListProps {
	relatedArticle?: Array<ArticleListResponseVo>
	areaIdx?: number
	query?: {
		code: string
		idx: string
	}
}
const RelatedArticleList = ({ relatedArticle, areaIdx, query }: RelatedArticleListProps) => {
	const cardProps = {
		layoutConfig: {
			layoutGap: { xs: pxToRem(12), md: pxToRem(16) },
		},
		thumbnailConfig: {
			thumbRatio: '16/9',
			badgeInset: pxToRem(6),
		},
		infoConfig: {
			infoSx: {
				'.info-title': {
					fontSize: { xs: pxToRem(16), md: pxToRem(17), lg: pxToRem(18) },
					lineHeight: { xs: pxToRem(22), md: pxToRem(22), lg: pxToRem(26) },
				},
			},
		},
	}
	const carouselConfig = {
		slidesPerView: 1.6,
		spaceBetween: 16,
		slidesOffsetBefore: 20,
		slidesOffsetAfter: 20,
		breakpoints: {
			768: {
				slidesPerView: 3,
				spaceBetween: 24,
				slidesOffsetBefore: 0,
				slidesOffsetAfter: 0,
			},
		},
		navPositionTop: pxToRem(63),
		sx: {
			position: 'relative',
			'.swiper-slide > div::after': {
				display: 'block',
				position: 'absolute',
				right: pxToRem(-8),
				top: 0,
				width: '1px',
				height: '100%',
				background: color.colGray3,
				[customTheme.mediaQueries.over.md]: {
					content: '""',
					right: pxToRem(-12),
				},
			},

			'.swiper-slide > div.last-slide::after': {
				height: '0 !important',
			},

			'.swiper-button-prev, .swiper-button-next': {
				top: {
					lg: `calc(${pxToRem(55)} - ${pxToRem(24)})`,
					xl: `calc(${pxToRem(71)} - ${pxToRem(24)})`,
				},
			},
		},
	}
	return (
		<Box>
			<SubSectionTitle>관련 기사</SubSectionTitle>
			<Box
				pt={{ xs: 0, md: pxToRem(16) }}
				borderTop={{ xs: 0, md: `1px solid ${color.colGray3}` }}
				mx={{ xs: pxToRem(-20), md: 0 }}
				sx={(theme) => ({
					'& .swiper:not(.swiper-initialized)': {
						[theme.breakpoints.down('md')]: {
							'& .swiper-wrapper': {
								paddingLeft: theme.pxToRem(20),
								paddingRight: theme.pxToRem(20),
								'&>*:not(:first-child)': {
									marginLeft: pxToRem(16),
								},
							},
							'& .swiper-slide': {
								width: `calc((100% - ${theme.pxToRem(16)}) / 1.6)`,
							},
						},
						[theme.breakpoints.up('md')]: {
							'& .swiper-wrapper': {
								'&>*:not(:first-child)': {
									marginLeft: pxToRem(24),
								},
							},
							'& .swiper-slide': {
								width: `calc((100% - ${theme.pxToRem(24 * 2)}) / 3)`,
							},
						},
					},
					'.swiper-button-next': {
						right: 0,
					},
					'.swiper-button-prev': {
						left: 0,
					},
				})}
			>
				<Carousel {...carouselConfig}>
					{relatedArticle?.map((item, index) => (
						<Fragment key={`article-${item.articleIdx}`}>
							{index !== 0 && (
								<UIDivider
									orientation="vertical"
									sx={{
										display: { xs: 'none', md: 'flex' },
									}}
								/>
							)}
							<ContentCard
								content={{
									articleIdx: item?.articleIdx,
									title: item?.articleTitle ?? '',
									href: getArticleLink(item, 'ARTICLE', areaIdx),
									query,
									src: item?.articleThumbnailImgUrl ?? imgDefaultH,
									alt: '',
									additionalText: getRelativeTime(item?.publicationDate),
									duration: item?.vodInfo?.playTime,
									bullet: item?.bulletType && item.bulletType.name,
									bulletColor:
										item?.bulletType && getBulletColor(item.bulletType.name),
								}}
								layoutConfig={cardProps.layoutConfig}
								thumbnailConfig={cardProps.thumbnailConfig}
								infoConfig={cardProps.infoConfig}
							/>
						</Fragment>
					))}
				</Carousel>
			</Box>
		</Box>
	)
}

export default RelatedArticleList
